import React, { useEffect, useState } from 'react';
import {
    FlexDiv,
    Heading2,
    Heading3,
    TextDefault,
    TextInput,
    TextTiny,
    ThemedButton,
    Tag,
    StyledIconButton,
    palette,
} from '@awarego-ui/components';
import { Alert } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

function domainOf(email) {
    return String(email || '')
        .trim()
        .toLowerCase()
        .split('@')[1];
}

export default function AwareExposeForm({
    config,
    defaultEmail,
    defaultName,
    error,
    onSubmit,
}) {
    const [name, setName] = useState(defaultName || '');
    const [title, setTitle] = useState('');
    const [email, setEmail] = useState(defaultEmail || '');
    const [linkInput, setLinkInput] = useState('');
    const [links, setLinks] = useState([]);
    const [nicknamesText, setNicknamesText] = useState('');

    useEffect(() => {
        if (defaultEmail && !email) setEmail(defaultEmail);
    }, [defaultEmail]);
    useEffect(() => {
        if (defaultName && !name) setName(defaultName);
    }, [defaultName]);

    const allowedAnyDomain = !!(config && config.allowedAnyDomain);
    const allowedDomains = (config && config.allowedDomains) || [];
    const emailDomain = domainOf(email);
    const emailValid =
        !!email &&
        !!emailDomain &&
        (allowedAnyDomain ||
            allowedDomains
                .map((d) => String(d).toLowerCase())
                .includes(emailDomain));

    const canSubmit = name.trim() && email.trim() && emailValid;

    const handleAddLink = () => {
        const next = linkInput.trim();
        if (!next) return;
        if (!links.includes(next)) {
            setLinks([...links, next]);
        }
        setLinkInput('');
    };

    const handleRemoveLink = (l) => setLinks(links.filter((x) => x !== l));

    const handleSubmit = (e) => {
        e.preventDefault && e.preventDefault();
        if (!canSubmit) return;
        const nicknames = nicknamesText
            .split(',')
            .map((n) => n.trim())
            .filter(Boolean);
        onSubmit({ name, title, email, links, nicknames });
    };

    return (
        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
            <FlexDiv column gap={6} mb={24}>
                <Heading2 displayBlock>See your threat profile</Heading2>
                <TextDefault color={palette.graphite.charcoal}>
                    Enter your details. The more accurate, the more realistic
                    your personalised threat profile and educational phishing
                    scenarios.
                </TextDefault>
            </FlexDiv>

            {!allowedAnyDomain && allowedDomains.length > 0 && (
                <FlexDiv alignCenter gap={8} mb={16} style={{ flexWrap: 'wrap' }}>
                    <TextTiny color={palette.graphite.charcoal}>
                        Only allowed email domains:
                    </TextTiny>
                    {allowedDomains.map((d) => (
                        <Tag
                            key={d}
                            text={d}
                            color={palette.evergreen.aspargus}
                            backgroundColor={palette.evergreen.mintLight}
                        />
                    ))}
                </FlexDiv>
            )}

            {error && (
                <Alert severity="error" style={{ marginBottom: 16 }}>
                    {error}
                </Alert>
            )}

            <FlexDiv column>
                <TextInput
                    label="Full name"
                    placeholder="e.g. Ragnar Sigurdsson"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    stretched
                />
                <TextInput
                    label="Job title"
                    placeholder="e.g. CEO & Co-founder"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    stretched
                />
                <TextInput
                    label="Work email"
                    type="email"
                    placeholder="yourname@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    error={!!email && !emailValid && !allowedAnyDomain}
                    helperText={
                        email &&
                        !emailValid &&
                        !allowedAnyDomain &&
                        allowedDomains.length
                            ? `Email must be on one of: ${allowedDomains.join(
                                  ', '
                              )}`
                            : undefined
                    }
                    stretched
                />
            </FlexDiv>

            <FlexDiv column gap={6} mt={24} mb={12}>
                <Heading3 displayBlock>Starting links (optional)</Heading3>
                <TextTiny color={palette.graphite.charcoal}>
                    Any public links you want us to use as a starting point —
                    for example LinkedIn, GitHub, your personal site, or a
                    podcast episode.
                </TextTiny>
            </FlexDiv>

            {links.length > 0 && (
                <FlexDiv column gap={6} mb={12} style={{ width: '100%' }}>
                    {links.map((l) => (
                        <FlexDiv
                            key={l}
                            alignCenter
                            gap={8}
                            padding={8}
                            borderRadius={4}
                            backgroundColor={palette.graphite.cloud}
                            style={{ width: '100%' }}
                        >
                            <TextDefault
                                color={palette.graphite.heavyMetal}
                                style={{
                                    flex: 1,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {l}
                            </TextDefault>
                            <StyledIconButton
                                onClick={() => handleRemoveLink(l)}
                                aria-label="Remove link"
                                marginRight="0"
                                size="small"
                            >
                                <DeleteOutlineIcon fontSize="small" />
                            </StyledIconButton>
                        </FlexDiv>
                    ))}
                </FlexDiv>
            )}

            <FlexDiv alignCenter gap={8}>
                <TextInput
                    placeholder="https://linkedin.com/in/yourprofile"
                    value={linkInput}
                    onChange={(e) => setLinkInput(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddLink();
                        }
                    }}
                    stretched
                />
                <ThemedButton
                    mode="secondary"
                    size="tiny"
                    $noTextTransform
                    icon={<AddIcon />}
                    iconPosition="start"
                    text="Add"
                    onClick={handleAddLink}
                    disabled={!linkInput.trim()}
                />
            </FlexDiv>

            <FlexDiv column gap={6} mt={24} mb={12}>
                <Heading3 displayBlock>
                    Nicknames / usernames (optional)
                </Heading3>
                <TextTiny color={palette.graphite.charcoal}>
                    Comma-separated. We&rsquo;ll automatically include the part
                    before <code>@</code> from your email and any slugs we can
                    detect in the links above (e.g. LinkedIn / GitHub / X). Add
                    any extras here — gamertags, handles you use across
                    communities, or older names you&rsquo;re known by.
                </TextTiny>
            </FlexDiv>

            <FlexDiv>
                <TextInput
                    placeholder="e.g. ragnarsig, ragnar_s, rsig"
                    value={nicknamesText}
                    onChange={(e) => setNicknamesText(e.target.value)}
                    stretched
                />
            </FlexDiv>

            <FlexDiv mt={32}>
                <ThemedButton
                    mode="primary"
                    text="Build my threat profile"
                    type="submit"
                    disabled={!canSubmit}
                    onClick={handleSubmit}
                />
            </FlexDiv>

            <FlexDiv mt={16} justifyCenter>
                <TextTiny
                    center
                    color={palette.graphite.charcoal}
                    style={{ display: 'block', maxWidth: 480 }}
                >
                    Your details are used only to generate this session.
                    AwareExpose helps you recognise targeted phishing — it does
                    not store any results when used anonymously.
                </TextTiny>
            </FlexDiv>
        </form>
    );
}
